import { useNostr } from '@nostrify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { NostrEvent, NostrFilter, NRelay } from '@nostrify/nostrify';
import { ARCHIVE_PUBKEY, isArchiveNote } from '@/hooks/useArchiveNotes';
import { useAppContext } from '@/hooks/useAppContext';
import { keepNote, isHiddenNote } from '@/lib/noteFilter';
import { verifyEvent } from 'nostr-tools';
import { PINNED_NOTE_IDS, PINNED_FALLBACKS, PINNED_LOOKUP_AUTHOR } from '@/lib/pinnedNotes';
import { PINNED_LOOKUP_RELAYS } from '@/lib/appRelays';
import { getRecoveredNotes } from '@/lib/recoveredNotes';

const PAGE_LIMIT = 500; // Per-request page size (relays may cap this lower)
const REQUEST_TIMEOUT = 15000; // Generous for slow mobile connections
const REQUEST_ATTEMPTS = 2; // Tries per page before giving up on a relay
const MAX_PAGES = 60; // Safety stop for runaway paging

const CACHE_KEY = `archive:events:${ARCHIVE_PUBKEY}`;

// Kinds the archive displays. Text notes & quote-posts are both kind 1.
const ARCHIVE_KINDS = [1];

type CachedEvent = Pick<
  NostrEvent,
  'id' | 'pubkey' | 'created_at' | 'kind' | 'content' | 'tags' | 'sig'
>;

function loadCache(): Map<string, CachedEvent> {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return new Map();
    const arr = JSON.parse(raw) as CachedEvent[];
    const map = new Map<string, CachedEvent>();
    for (const ev of arr) {
      if (ev && typeof ev.id === 'string') map.set(ev.id, ev);
    }
    return map;
  } catch {
    return new Map();
  }
}

/**
 * A note from the archive's saved copy (everything this browser has ever
 * fetched for the feed), or null. Lets a record open from the feed even when no
 * relay will serve it right now.
 */
export function findCachedArchiveEvent(id: string): NostrEvent | null {
  const ev = loadCache().get(id);
  return ev && ev.pubkey === ARCHIVE_PUBKEY ? (ev as NostrEvent) : null;
}

function saveCache(map: Map<string, CachedEvent>): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(Array.from(map.values())));
  } catch {
    /* non-fatal */
  }
}

function toCached(ev: NostrEvent): CachedEvent {
  return {
    id: ev.id,
    pubkey: ev.pubkey,
    created_at: ev.created_at,
    kind: ev.kind,
    content: ev.content,
    tags: ev.tags,
    sig: ev.sig,
  };
}

/** Normalise a relay URL so duplicates (trailing slash etc.) collapse. */
function normalizeRelayUrl(url: string): string | null {
  try {
    return new URL(url).href;
  } catch {
    return null;
  }
}

/**
 * Pull ONE relay's full history for the archive author, newest first, paging
 * backwards until the relay has nothing new to give.
 *
 * Each relay is queried on its own (rather than through the pool) because the
 * pool stops waiting shortly after the FIRST relay finishes and discards
 * everything the slower relays would have returned — which silently drops
 * notes. A single relay gives an ordered, complete answer, so cursor paging is
 * safe here: the cursor is inclusive (`until = oldest`) so notes sharing a
 * timestamp are never skipped, and paging stops once a page adds nothing new.
 */
async function fetchRelayHistory(
  relay: NRelay,
  outerSignal: AbortSignal | undefined,
  onEvents: (events: NostrEvent[]) => void,
): Promise<void> {
  const seen = new Set<string>();
  let until: number | undefined;

  for (let page = 0; page < MAX_PAGES; page++) {
    if (outerSignal?.aborted) return;

    const filter: NostrFilter = {
      kinds: ARCHIVE_KINDS,
      authors: [ARCHIVE_PUBKEY],
      limit: PAGE_LIMIT,
      ...(until !== undefined ? { until } : {}),
    };

    let events: NostrEvent[] | undefined;
    for (let attempt = 0; attempt < REQUEST_ATTEMPTS && !events; attempt++) {
      if (outerSignal?.aborted) return;
      try {
        events = await relay.query([filter], {
          signal: AbortSignal.any([
            AbortSignal.timeout(REQUEST_TIMEOUT),
            ...(outerSignal ? [outerSignal] : []),
          ]),
        });
      } catch {
        /* retry, then give up on this relay */
      }
    }
    if (!events || events.length === 0) return;

    let fresh = 0;
    let oldest = Infinity;
    for (const ev of events) {
      if (!seen.has(ev.id)) {
        seen.add(ev.id);
        fresh++;
      }
      if (ev.created_at < oldest) oldest = ev.created_at;
    }

    onEvents(events);
    if (fresh === 0) return; // no progress — we've reached the end
    until = oldest;
  }
}

/**
 * Loads the complete archive: the master total AND the full note list that the
 * feed renders (classification into terms happens client-side, see
 * lib/noteTags.ts). This is the single source of truth for "what's in the
 * archive".
 *
 * Backfill strategy — every read relay (plus a wide set of deep-history
 * relays) is asked for the author's complete history independently and in
 * parallel, and the answers are unioned. There is no date floor, so old or
 * back-dated notes are found too. Results stream into the page as each relay
 * answers, and are merged into a persisted localStorage cache so the set only
 * ever grows and converges across devices/sessions.
 */
export function useArchiveTagCounts() {
  const { nostr } = useNostr();
  const { config } = useAppContext();
  const queryClient = useQueryClient();

  return useQuery<{
    total: number;
    notes: NostrEvent[];
  }>({
    queryKey: ['archive', 'tag-counts', ARCHIVE_PUBKEY],
    queryFn: async ({ signal }) => {
      const byId = loadCache();
      const queryKey = ['archive', 'tag-counts', ARCHIVE_PUBKEY];

      const snapshot = () => {
        const notes = (Array.from(byId.values()) as NostrEvent[])
          .filter((ev) => isArchiveNote(ev) && keepNote(ev))
          .sort((a, b) => b.created_at - a.created_at);
        return { total: notes.length, notes };
      };

      // Every relay we can read from, plus the deep-history set, deduplicated.
      const urls = new Set<string>();
      for (const r of config.relayMetadata.relays) {
        if (!r.read) continue;
        const u = normalizeRelayUrl(r.url);
        if (u) urls.add(u);
      }
      for (const raw of PINNED_LOOKUP_RELAYS) {
        const u = normalizeRelayUrl(raw);
        if (u) urls.add(u);
      }

      // Ask each relay for the full history in parallel. As each page lands,
      // publish what we have so far so the feed fills in progressively instead
      // of waiting on the slowest relay.
      await Promise.allSettled(
        Array.from(urls).map((url) =>
          fetchRelayHistory(nostr.relay(url), signal ?? undefined, (events) => {
            let added = false;
            for (const ev of events) {
              if (ev.pubkey !== ARCHIVE_PUBKEY) continue;
              if (!byId.has(ev.id)) {
                byId.set(ev.id, toCached(ev));
                added = true;
              }
            }
            if (added && !signal?.aborted) {
              queryClient.setQueryData(queryKey, snapshot());
            }
          }),
        ),
      );

      // Records the relays no longer serve, kept as signed copies in the site
      // itself (lib/recoveredNotes.ts). Filed like any other record.
      for (const ev of getRecoveredNotes()) {
        if (!byId.has(ev.id)) byId.set(ev.id, toCached(ev));
      }

      // Guarantee curated "must-have" notes are present even if no relay
      // surfaced them. Fetch them directly by id (widest possible net) and
      // union into the cache. These are hand-curated, so we trust the curator:
      // no author filter, and they bypass the reply/empty display filter below.
      const pinnedIds = new Set<string>();
      if (PINNED_NOTE_IDS.length > 0 && !signal?.aborted) {
        try {
          // Use a dedicated deep-history relay group with a long timeout, since
          // the default pool's short EOSE window misses slow relays.
          const group = nostr.group(PINNED_LOOKUP_RELAYS);
          const pinned = await group.query([{ ids: PINNED_NOTE_IDS }], {
            signal: AbortSignal.any([
              AbortSignal.timeout(20000),
              ...(signal ? [signal] : []),
            ]),
          });
          for (const ev of pinned) {
            byId.set(ev.id, toCached(ev));
            pinnedIds.add(ev.id);
          }
        } catch {
          /* non-fatal — embedded fallbacks below still cover pinned notes */
        }
      }

      // Embedded fallbacks for pinned notes relays didn't return.
      for (const fb of PINNED_FALLBACKS) {
        const ev = fb.event;
        if (pinnedIds.has(ev.id)) continue;
        if (fb.verified) {
          try {
            if (verifyEvent(ev)) {
              byId.set(ev.id, toCached(ev));
              pinnedIds.add(ev.id);
            }
          } catch {
            /* ignore malformed fallback */
          }
        } else if (ev.pubkey === PINNED_LOOKUP_AUTHOR) {
          byId.set(ev.id, toCached(ev));
          pinnedIds.add(ev.id);
        }
      }

      saveCache(byId);

      // Curated (pinned) notes always stay; everything else must be a real
      // archive note AND pass the link/dead-note filter (see lib/noteFilter.ts).
      const displayable = (Array.from(byId.values()) as NostrEvent[])
        .filter((ev) => !isHiddenNote(ev))
        .filter((ev) => pinnedIds.has(ev.id) || (isArchiveNote(ev) && keepNote(ev)))
        .sort((a, b) => b.created_at - a.created_at);
      return { total: displayable.length, notes: displayable };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: true,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
  });
}
